import { t } from "@lingui/macro";
import type { Scalars } from "@src/__generated__/graphql";
import {
  Select,
  type SelectProps,
  type SelectRef,
} from "@src/components/ui-kit";
import type { toWorkTypeOptionsParams } from "@src/utils/map-to-options/workTypes";
import { observer, useLocalObservable } from "mobx-react-lite";
import { forwardRef, useEffect } from "react";

import { PositionSelectStore } from "./store";

type PositionSelectProps = Omit<SelectProps, "options"> & {
  readonly optionModifier?: toWorkTypeOptionsParams["modifier"];
  readonly options?: SelectProps["options"];
  readonly projectId?: Scalars["ID"]["output"];
};

export const PositionSelect = observer(
  forwardRef<SelectRef, PositionSelectProps>(function PositionSelect(
    { optionModifier, options: passedOptions, projectId, ...props },
    ref,
  ) {
    const { store } = useLocalObservable(() => ({
      store: new PositionSelectStore(),
    }));

    useEffect(() => {
      if (passedOptions) {
        store.clearOptions();
        return;
      }

      if (projectId) {
        store.fetchProjectPositions(projectId, optionModifier);
      } else {
        store.fetchPositionOptions(optionModifier);
      }
    }, [
      projectId,
      passedOptions,
      store.fetchPositionOptions,
      store.fetchProjectPositions,
      optionModifier,
    ]);

    return (
      <Select
        ref={ref}
        isLoading={store.isLoading}
        placeholder={t`Select position`}
        options={passedOptions ?? store.options}
        {...props}
      />
    );
  }),
);
